import React, { useState } from 'react';
import { useGameLounge } from '../context/GameLoungeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaPlaystation } from 'react-icons/fa';

const { FiSettings, FiDollarSign, FiPlus, FiEdit2, FiTrash2, FiSave, FiMonitor, FiHome, FiX } = FiIcons;

function Settings() {
  const { state, dispatch } = useGameLounge();
  const [settings, setSettings] = useState(state.settings);
  const [newItem, setNewItem] = useState({ name: '', price: '', category: 'Snacks' });
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Room management states
  const [showAddRoomForm, setShowAddRoomForm] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [editingRoom, setEditingRoom] = useState(null);
  const [selectedRoomForStations, setSelectedRoomForStations] = useState(null);

  const handleSettingsChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const saveSettings = () => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
    alert('Einstellungen gespeichert!');
  };

  const handleAddItem = () => {
    if (newItem.name && newItem.price && newItem.category) {
      dispatch({
        type: 'ADD_KIOSK_ITEM',
        payload: {
          ...newItem,
          price: parseFloat(newItem.price)
        }
      });
      setNewItem({ name: '', price: '', category: 'Snacks' });
      setShowAddForm(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({ ...item });
  };

  const saveEditItem = () => {
    dispatch({
      type: 'UPDATE_KIOSK_ITEM',
      payload: editingItem
    });
    setEditingItem(null);
  };

  const deleteItem = (itemId) => {
    if (confirm('Artikel wirklich löschen?')) {
      dispatch({
        type: 'DELETE_KIOSK_ITEM',
        payload: { id: itemId }
      });
    }
  };

  // Room management functions
  const handleAddRoom = () => {
    if (newRoomName.trim()) {
      dispatch({
        type: 'ADD_ROOM',
        payload: { name: newRoomName.trim() }
      });
      setNewRoomName('');
      setShowAddRoomForm(false);
    }
  };

  const handleUpdateRoom = (roomId) => {
    if (editingRoom && editingRoom.name.trim()) {
      dispatch({
        type: 'UPDATE_ROOM',
        payload: { roomId, name: editingRoom.name.trim() }
      });
      setEditingRoom(null);
    }
  };

  const handleDeleteRoom = (roomId) => {
    const room = state.rooms.find(r => r.id === roomId);
    if (room.stations.length > 0) {
      if (!confirm(`Raum "${room.name}" enthält ${room.stations.length} Station(en). Wirklich löschen?`)) {
        return;
      }
    }
    dispatch({
      type: 'DELETE_ROOM',
      payload: { roomId }
    });
  };

  const handleAddStation = (roomId, type) => {
    dispatch({
      type: 'ADD_STATION_TO_ROOM',
      payload: { roomId, type }
    });
  };

  const handleRemoveStation = (roomId, stationId) => {
    if (confirm('Station wirklich entfernen?')) {
      dispatch({
        type: 'REMOVE_STATION_FROM_ROOM',
        payload: { roomId, stationId }
      });
    }
  };

  const categories = ['Snacks', 'Pizza', 'Getränke', 'Sonstiges'];

  const getRoomStats = (room) => {
    const pcCount = room.stations.filter(s => s.type === 'pc').length;
    const psCount = room.stations.filter(s => s.type === 'playstation').length;
    return { pcCount, psCount, total: room.stations.length };
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Einstellungen</h1>
        <p className="text-gray-600">Verwalten Sie Räume, Preise und Kiosk-Artikel</p>
      </div>

      {/* Room Management */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <SafeIcon icon={FiHome} />
            <span>Raumverwaltung</span>
          </h2>
          <button
            onClick={() => setShowAddRoomForm(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Raum hinzufügen</span>
          </button>
        </div>

        {/* Add Room Form */}
        {showAddRoomForm && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Neuen Raum hinzufügen</h3>
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Raumname (z.B. Raum 4, VIP Lounge)"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddRoom()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <button
                onClick={handleAddRoom}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
              >
                Hinzufügen
              </button>
              <button
                onClick={() => {
                  setShowAddRoomForm(false);
                  setNewRoomName('');
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Rooms List */}
        <div className="space-y-4">
          {state.rooms.map(room => {
            const stats = getRoomStats(room);
            const isExpanded = selectedRoomForStations === room.id;
            
            return (
              <div key={room.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      {editingRoom && editingRoom.id === room.id ? (
                        <div className="flex items-center space-x-2 flex-1">
                          <input
                            type="text"
                            value={editingRoom.name}
                            onChange={(e) => setEditingRoom({ ...editingRoom, name: e.target.value })}
                            className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={() => handleUpdateRoom(room.id)}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded transition-colors"
                          >
                            <SafeIcon icon={FiSave} />
                          </button>
                          <button
                            onClick={() => setEditingRoom(null)}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded transition-colors"
                          >
                            <SafeIcon icon={FiX} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-lg font-semibold text-gray-900">{room.name}</h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiMonitor} className="text-blue-500" />
                              <span>{stats.pcCount} PCs</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FaPlaystation} className="text-blue-700" />
                              <span>{stats.psCount} PlayStations</span>
                            </div>
                            <span className="text-gray-400">|</span>
                            <span className="font-medium">{stats.total} Gesamt</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingRoom({ id: room.id, name: room.name })}
                        className="text-blue-600 hover:text-blue-800 p-2"
                        title="Raum bearbeiten"
                      >
                        <SafeIcon icon={FiEdit2} />
                      </button>
                      <button
                        onClick={() => handleDeleteRoom(room.id)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Raum löschen"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                      <button
                        onClick={() => setSelectedRoomForStations(isExpanded ? null : room.id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded transition-colors text-sm"
                      >
                        {isExpanded ? 'Schließen' : 'Stationen verwalten'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Station Management for Room */}
                {isExpanded && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">Stationen in {room.name}</h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleAddStation(room.id, 'pc')}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm flex items-center space-x-1 transition-colors"
                        >
                          <SafeIcon icon={FiMonitor} className="text-sm" />
                          <span>PC hinzufügen</span>
                        </button>
                        <button
                          onClick={() => handleAddStation(room.id, 'playstation')}
                          className="bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded text-sm flex items-center space-x-1 transition-colors"
                        >
                          <SafeIcon icon={FaPlaystation} className="text-sm" />
                          <span>PlayStation hinzufügen</span>
                        </button>
                      </div>
                    </div>

                    {room.stations.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>Keine Stationen vorhanden</p>
                        <p className="text-sm">Fügen Sie PCs oder PlayStations hinzu</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {room.stations.map(station => (
                          <div
                            key={station.id}
                            className="border border-gray-200 rounded-lg p-3 flex items-center justify-between bg-gray-50"
                          >
                            <div className="flex items-center space-x-2">
                              <SafeIcon 
                                icon={station.type === 'pc' ? FiMonitor : FaPlaystation} 
                                className={`text-lg ${station.type === 'pc' ? 'text-blue-500' : 'text-blue-700'}`}
                              />
                              <div>
                                <p className="font-medium text-gray-900">{station.name}</p>
                                <p className="text-xs text-gray-500 capitalize">{station.type}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveStation(room.id, station.id)}
                              disabled={station.status !== 'available'}
                              className={`p-1 rounded transition-colors ${
                                station.status === 'available'
                                  ? 'text-red-600 hover:text-red-800 hover:bg-red-50'
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                              title={station.status !== 'available' ? 'Station ist aktiv und kann nicht entfernt werden' : 'Station entfernen'}
                            >
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {state.rooms.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>Keine Räume vorhanden</p>
            <p className="text-sm">Fügen Sie einen neuen Raum hinzu, um zu beginnen</p>
          </div>
        )}
      </div>

      {/* Pricing Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
          <SafeIcon icon={FiDollarSign} />
          <span>Stundentarife</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-blue-500 p-3 rounded-lg">
                <SafeIcon icon={FiMonitor} className="text-2xl text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Gaming PC</h3>
                <p className="text-sm text-gray-600">Stundensatz festlegen</p>
              </div>
            </div>
            <div className="relative">
              <input
                type="number"
                step="0.50"
                value={settings.pcHourlyRate}
                onChange={(e) => handleSettingsChange('pcHourlyRate', parseFloat(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-semibold"
              />
              <span className="absolute right-4 top-3 text-gray-500 font-medium">
                {state.settings.currency} / Stunde
              </span>
            </div>
          </div>
          
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-blue-700 p-3 rounded-lg">
                <SafeIcon icon={FaPlaystation} className="text-2xl text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">PlayStation</h3>
                <p className="text-sm text-gray-600">Stundensatz pro Controller</p>
              </div>
            </div>
            <div className="relative">
              <input
                type="number"
                step="0.50"
                value={settings.playstationHourlyRate}
                onChange={(e) => handleSettingsChange('playstationHourlyRate', parseFloat(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-semibold"
              />
              <span className="absolute right-4 top-3 text-gray-500 font-medium">
                {state.settings.currency} / Stunde
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-700 mb-2">Preisbeispiele:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Gaming PC - 2 Stunden:</p>
              <p className="text-lg font-bold text-blue-600">
                {(settings.pcHourlyRate * 2).toFixed(2)} {state.settings.currency}
              </p>
            </div>
            <div>
              <p className="text-gray-600">PlayStation - 2 Stunden (2 Controller):</p>
              <p className="text-lg font-bold text-blue-700">
                {(settings.playstationHourlyRate * 2 * 2).toFixed(2)} {state.settings.currency}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={saveSettings}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md font-medium transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiSave} />
            <span>Einstellungen speichern</span>
          </button>
        </div>
      </div>

      {/* Kiosk Items */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <SafeIcon icon={FiSettings} />
            <span>Kiosk Artikel</span>
          </h2>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Artikel hinzufügen</span>
          </button>
        </div>

        {/* Add Item Form */}
        {showAddForm && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Neuen Artikel hinzufügen</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Artikelname"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                step="0.50"
                placeholder="Preis"
                value={newItem.price}
                onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <div className="flex space-x-2">
                <button
                  onClick={handleAddItem}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors"
                >
                  Hinzufügen
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Items List */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Artikel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategorie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.kioskItems.map(item => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem && editingItem.id === item.id ? (
                      <input
                        type="text"
                        value={editingItem.name}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem && editingItem.id === item.id ? (
                      <select
                        value={editingItem.category}
                        onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm text-gray-600">{item.category}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem && editingItem.id === item.id ? (
                      <input
                        type="number"
                        step="0.50"
                        value={editingItem.price}
                        onChange={(e) => setEditingItem({ ...editingItem, price: parseFloat(e.target.value) })}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-20"
                      />
                    ) : (
                      <span className="text-sm font-medium text-green-600">
                        {item.price.toFixed(2)} {state.settings.currency}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {editingItem && editingItem.id === item.id ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={saveEditItem}
                          className="text-green-600 hover:text-green-900"
                        >
                          <SafeIcon icon={FiSave} />
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditItem(item)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <SafeIcon icon={FiEdit2} />
                        </button>
                        <button
                          onClick={() => deleteItem(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Settings;